import { Market } from "@openbook-dex/openbook";
import { WalletAdapter } from "@solana/wallet-adapter-base";
import { Account, Connection, PublicKey, Transaction } from "@solana/web3.js";
import { notify } from "./notify";
import {
  Token,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import { sendWalletAllTransactions, sendWalletTransaction } from "./transaction";
import { WalletContextState } from "@solana/wallet-adapter-react";
import toast from 'react-hot-toast';
import { GetTokenBalance } from "helpers/helpers";
import { BN } from "@coral-xyz/anchor";
import { encodeInstruction } from "@openbook-dex/openbook/lib/instructions";
import { DEX_PID } from "ts/accountInfo/openOrders";

export async function placeOrder({
  side,
  price,
  size,
  orderType,
  market,
  connection,
  wallet,
  baseCurrencyAccount,
  quoteCurrencyAccount,
  feeDiscountPubkey = undefined,
  toastId
}: {
  side: 'buy' | 'sell';
  price: number;
  size: number;
  orderType: 'ioc' | 'postOnly' | 'limit';
  market: Market | undefined | null;
  connection: Connection;
  wallet: WalletAdapter;
  baseCurrencyAccount: PublicKey | undefined;
  quoteCurrencyAccount: PublicKey | undefined;
  feeDiscountPubkey: PublicKey | undefined;
  toastId: any
}) {
  let formattedMinOrderSize =
    market?.minOrderSize?.toFixed(getDecimalCount(market.minOrderSize)) ||
    market?.minOrderSize;
  let formattedTickSize =
    market?.tickSize?.toFixed(getDecimalCount(market.tickSize)) ||
    market?.tickSize;
  const isIncrement = (num, step) =>
    Math.abs((num / step) % 1) < 1e-5 ||
    Math.abs(((num / step) % 1) - 1) < 1e-5;
  if (isNaN(price)) {
    notify({ message: 'Invalid price', type: 'error' });
    toast.error('Invalid price specified', {
      id: toastId,
    });
    return;
  }
  if (isNaN(size)) {
    notify({ message: 'Invalid size', type: 'error' });
    toast.error('Invalid trade amount specified', {
      id: toastId,
    });
    return;
  }
  if (!wallet || !wallet.publicKey) {
    notify({ message: 'Connect wallet', type: 'error' });
    toast.error('Please connect a wallet!', {
      id: toastId,
    });
    return;
  }
  if (!market) {
    notify({ message: 'Invalid  market', type: 'error' });
    toast.error('Selected market is invalid!', {
      id: toastId,
    });
    return;
  }
  // if ((!isIncrement(size, market.minOrderSize)) && (side == 'sell' || orderType == 'limit')) {
  //   notify({
  //     message: `Size must be an increment of ${formattedMinOrderSize}`,
  //     type: 'error',
  //   });
  //   toast.error(`Trade amount must be an increment of ${formattedMinOrderSize}`, {
  //     id: toastId,
  //   });
  //   return;
  // }
  if ((size < market.minOrderSize) && (side == 'sell' || orderType == 'limit')) {
    notify({ message: 'Size too small', type: 'error' });
    toast.error(`Trade amount is too small!`, {
      id: toastId,
    });
    return;
  }
  if (!isIncrement(price, market.tickSize)) {
    notify({
      message: `Price must be an increment of ${formattedTickSize}`,
      type: 'error',
    });
    toast.error(`Limit price must be an increment of ${formattedTickSize}`, {
      id: toastId,
    });
    return;
  }
  if (price < market.tickSize) {
    notify({ message: 'Price under tick size', type: 'error' });
    toast.error(`Limit price is under the minimum tick size!`, {
      id: toastId,
    });
    return;
  }
  const owner = wallet.publicKey;
  const transaction = new Transaction();
  const signers:Account[] = [];

  if (!baseCurrencyAccount) {
    const {
      transaction: createAccountTransaction,
      newAccountPubkey,
    } = await createTokenAccountTransaction({
      connection,
      wallet,
      mintPublicKey: market.baseMintAddress,
    });
    transaction.add(createAccountTransaction);
    baseCurrencyAccount = newAccountPubkey;
  }
  if (!quoteCurrencyAccount) {
    const {
      transaction: createAccountTransaction,
      newAccountPubkey,
    } = await createTokenAccountTransaction({
      connection,
      wallet,
      mintPublicKey: market.quoteMintAddress,
    });
    transaction.add(createAccountTransaction);
    quoteCurrencyAccount = newAccountPubkey;
  }

  const payer = side === 'sell' ? baseCurrencyAccount : quoteCurrencyAccount;
  if (!payer) {
    notify({
      message: 'Need an SPL token account for cost currency',
      type: 'error',
    });
    return;
  }
  const params = {
    owner,
    payer,
    side,
    price,
    size,
    orderType,
    feeDiscountPubkey: feeDiscountPubkey || null,
  };
  console.log(params);

  try {
    const { transaction: placeOrderTx, signers } =
      await market.makePlaceOrderTransaction(
        connection,
        {
          owner: wallet.publicKey,
          payer,
          side: side,
          price: price,
          size: (orderType == "ioc" && side == "buy") ?  market.minOrderSize : size,
          orderType: orderType,
          selfTradeBehavior: "decrementTake",
          feeDiscountPubkey: null,
        },
        120_000,
        120_000
      );
      toast.loading('Sending Transaction...', {
        id: toastId,
      });
    transaction.add(placeOrderTx);
    if (orderType == "ioc" && side == "buy") {
      let id = 0;
      while (transaction.instructions[id].keys.length < 10) id++;
      transaction.instructions[id].data = encodeInstruction({
        newOrderV3: {
          side: side,
          limitPrice:new BN(1<<60),
          maxBaseQuantity: new BN(1<<60), //@ts-ignore
          maxQuoteQuantity: market.quoteSizeNumberToLots(size * market._decoded.quoteLotSize.toNumber()),
          selfTradeBehavior: 'decrementTake',
          orderType: 'ioc',
          clientId: new BN(0),
          limit: new BN(65535),
      }
      });
    }
    const txSig = await sendWalletTransaction(
      connection,
      transaction,
      wallet,
      signers
    );
    console.log(txSig);
    toast.success('Transaction Complete!', {
      id: toastId,
    });
    return {
      type: "success",
      message:"Transaction Complete!"
    }

  } catch (e:any) {
    toast.error('Transaction cancelled! ' + e.message, {
      id: toastId,
    });
  }

  // let {
  //   transaction: placeOrderTx,
  //   signers: placeOrderSigners,
  // } = await market.makePlaceOrderTransaction(
  //   connection,
  //   params,
  //   120_000,
  //   120_000,
  // ).catch(e => {console.log("ERROR", e); return {transaction: new Transaction(), signers: []}});
  // const endTime = getUnixTs();
  // console.log(`Creating order transaction took ${endTime - startTime}`);
  // transaction.add(placeOrderTx);
  // transaction.add(market.makeMatchOrdersTransaction(5));
  // signers.push(...placeOrderSigners);

  // return await wallet.sendTransaction({
  //   transaction,
  //   wallet,
  //   connection,
  //   signers,
  //   sendingMessage: 'Sending order...',
  // });
  // return await wallet.sendTransaction(
  //   transaction,
  //   connection
  // )
}


export async function createTokenAccountTransaction({
  connection,
  wallet,
  mintPublicKey,
}: {
  connection: Connection;
  wallet: WalletAdapter;
  mintPublicKey: PublicKey;
}): Promise<{
  transaction: Transaction;
  newAccountPubkey: PublicKey;
}> {
  // 👇️ ts-ignore ignores any ts errors on the next line
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const ata = await Token.getAssociatedTokenAddress(ASSOCIATED_TOKEN_PROGRAM_ID,TOKEN_PROGRAM_ID, mintPublicKey,wallet.publicKey);
  const transaction = new Transaction();
  transaction.add(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    Token.createAssociatedTokenAccountInstruction(ASSOCIATED_TOKEN_PROGRAM_ID,TOKEN_PROGRAM_ID,mintPublicKey,ata,wallet.publicKey,wallet.publicKey,),
  );
  return {
    transaction,
    newAccountPubkey: ata,
  };
}


export function getDecimalCount(value): number {
  if (
    !isNaN(value) &&
    Math.floor(value) !== value &&
    value.toString().includes('.')
  )
    return value.toString().split('.')[1].length || 0;
  if (
    !isNaN(value) &&
    Math.floor(value) !== value &&
    value.toString().includes('e')
  )
    return parseInt(value.toString().split('e-')[1] || '0');
  return 0;
}

export const getUnixTs = () => {
  return new Date().getTime() / 1000;
};


export const cancelOrders = async(connection, wallet, orders) => {
  let toastId = toast.loading('Cancelling Order...');
  let all: any[] = [];
  try {
  for (let i = 0; i < orders.length; i++) {
    let order = orders[i];
    let market = order.loadedMarket;
  try {
    const tx = await market.makeCancelOrderTransaction(
      connection,
      wallet.publicKey,
      order
    );
    all.push({tx: tx, signers: undefined});
  
  } catch(e: any) {
    console.log("error + cancel order", e.message)
  }
  }
  const txSig = await sendWalletAllTransactions(connection, wallet, all);
  console.log(txSig);
  toast.success('Order cancelled!',{
    id: toastId
  });
  return {
    type: "success",
    toastId: toastId,
    tx: txSig[0],
  }
}catch(e:any) {
  console.log("error", e.message)
  toast.error("Couldn't cancel order! " + e.message,{
    id: toastId
  });
}
}

export const settleFunds = async(connection, marketData, userAccounts, wallet, toastId=undefined) => {

  if(!wallet.publicKey || !connection || !marketData.serumMarket || !userAccounts) {
    toast.error('Please connect a wallet!',{
      id: toastId
    });
    return;
  }
  let baseAssocAddress = GetTokenBalance(marketData.market.baseMint.toBase58(), userAccounts).address;
  let quoteAssocAddress = GetTokenBalance(marketData.market.quoteMint.toBase58(), userAccounts).address;
  toast.loading('Settling Funds...',{
    id: toastId
  });
  for (let openOrdersAccount of await marketData.serumMarket.findOpenOrdersAccountsForOwner(
    connection,
    wallet.publicKey,
  )) {
    // if (openOrdersAccount.baseTokenFree.toNumber() > 0 || openOrdersAccount.quoteTokenFree.toNumber() > 0) 
    {
      let baseTokenAccount = new PublicKey(baseAssocAddress);
      let quoteTokenAccount = new PublicKey(quoteAssocAddress);
      try {
        
        const HOST = new PublicKey("Bh2A3uVmMaVK5fYVmgtmAADeVgiSCrWFQuorY6tCRGm");
        const hostAta = await Token.getAssociatedTokenAddress(
          ASSOCIATED_TOKEN_PROGRAM_ID,TOKEN_PROGRAM_ID,marketData.market.quoteMint,HOST
        );
        let info = await connection.getAccountInfo(hostAta);

        const { transaction, signers } = await marketData.serumMarket.makeSettleFundsTransaction(
          connection,
          openOrdersAccount,
          baseTokenAccount,
          quoteTokenAccount,
          hostAta,
        );

        if (!info)
          transaction.instructions = [
            Token.createAssociatedTokenAccountInstruction(
              ASSOCIATED_TOKEN_PROGRAM_ID,TOKEN_PROGRAM_ID,marketData.market.quoteMint,hostAta,HOST,wallet.publicKey
            ),
            ...transaction.instructions
          ];
  
        const txSig = await sendWalletTransaction(
          connection,
          transaction,
          wallet,
          signers
        );
        
        toast.success('Funds settled!',{
          id: toastId
        });
        return {
          type: "success",
          toastId: toastId,
          tx: txSig
        }
      } catch(e:any) {
        console.log("error", e.message)
        toast.error("Couldn't settle funds! " + e.message,{
          id: toastId
        });
      }
    }
  }
  toast.error("No funds need to be settled! ",{
    id: toastId
  });

  
}

export const settleFundsCustom = async(
  connection,
  userAccounts,
  toSettle,
  wallet,
  toastId=undefined
) => {

  if(!wallet.publicKey || !connection || !userAccounts) {
    toast.error('Please connect a wallet!',{
      id: toastId
    });
    return;
  }
  toast.loading('Settling Funds...',{
    id: toastId
  });
try {
  let all: any[] = [];
  for (let i = 0; i < toSettle.length; i++) {
    let marketData = await Market.load(connection, toSettle[i].object.market, undefined, DEX_PID);
  let baseAssocAddress = GetTokenBalance(toSettle[i].baseMint.toBase58(), userAccounts).address;
  let quoteAssocAddress = GetTokenBalance(toSettle[i].quoteMint.toBase58(), userAccounts).address;

  let baseTokenAccount = new PublicKey(baseAssocAddress);
  let quoteTokenAccount = new PublicKey(quoteAssocAddress);
  try {
    
    const HOST = new PublicKey("Bh2A3uVmMaVK5fYVmgtmAADeVgiSCrWFQuorY6tCRGm");
    const hostAta = await Token.getAssociatedTokenAddress(
      ASSOCIATED_TOKEN_PROGRAM_ID,TOKEN_PROGRAM_ID,toSettle[i].quoteMint,HOST
    );
    const { transaction, signers } = await marketData.makeSettleFundsTransaction(
      connection,
      toSettle[i].object,
      baseTokenAccount,
      quoteTokenAccount,
      hostAta,
    );

    let info = await connection.getAccountInfo(hostAta);
    if (!info)
      transaction.instructions = [
        Token.createAssociatedTokenAccountInstruction(
          ASSOCIATED_TOKEN_PROGRAM_ID,TOKEN_PROGRAM_ID,toSettle[i].quoteMint,hostAta,HOST,wallet.publicKey
        ),
        ...transaction.instructions
      ];

    all.push({
      tx: transaction,
      signers: signers
    });
  } catch(e:any) {
    console.log("error", e.message)
    toast.error("Couldn't settle funds! " + e.message,{
      id: toastId
    });
  }
}
const txSig = await sendWalletAllTransactions(
  connection,
  wallet,
  all,
);
console.log(txSig);

toast.success('Funds settled!',{
  id: toastId
});
return {
  type: "success",
  toastId: toastId,
  tx: txSig[0]
}
} catch(e) {
  console.log(e);
  toast.error("No funds need to be settled! ",{
    id: toastId
  });
}
}